// LT-534 — reusable searchable / filterable multi-select contact picker.
//
// Used by the campaign audience step (wizard + flat form) and the Contacts
// page "Add to campaign" bulk action. Selection persists across searches
// (a Map keyed by contact id) so an operator can refine the query, tick a
// few more, and confirm the union. Returns the selected Contact[] to the
// caller, which decides what to do with them (materialise a list, etc.).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';

import { contactsAPI } from '../services/contactsAPI';
import { accountsAPI } from '../services/accountsAPI';
import type { Account, Contact, LifecycleStage } from '../types/contact';
import { LIFECYCLE_STAGES, LIFECYCLE_STAGE_LABELS } from '../types/contact';
import { Button, IconButton, Select } from '../ui';

function displayName(c: Contact): string {
  const full = [c.first_name, c.last_name].filter(Boolean).join(' ').trim();
  return full || c.email || c.phone || 'Unnamed contact';
}

export interface ContactPickerModalProps {
  open: boolean;
  onClose: () => void;
  /** Called with the chosen contacts when the operator confirms. May be
   * async — the modal shows a busy state and stays open until it resolves so
   * a caller that creates a list / campaign can surface errors. */
  onConfirm: (contacts: Contact[]) => void | Promise<void>;
  title?: string;
  description?: React.ReactNode;
  confirmLabel?: string;
  /** External busy flag (e.g. the caller is creating a list). Disables the
   * controls and shows a spinner label on the confirm button. */
  busy?: boolean;
  /** External error from the caller's confirm handler. */
  error?: string | null;
}

const ContactPickerModal: React.FC<ContactPickerModalProps> = ({
  open,
  onClose,
  onConfirm,
  title = 'Pick contacts',
  description,
  confirmLabel = 'Add selected',
  busy = false,
  error = null,
}) => {
  const [query, setQuery] = useState('');
  const [lifecycle, setLifecycle] = useState<'' | LifecycleStage>('');
  const [accountId, setAccountId] = useState('');
  const [results, setResults] = useState<Contact[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(false);
  const [localErr, setLocalErr] = useState<string | null>(null);
  // Map<id, Contact> so the selection survives across different result sets.
  const [selected, setSelected] = useState<Map<string, Contact>>(new Map());

  const runSearch = useCallback(async () => {
    setLoading(true);
    setLocalErr(null);
    try {
      const rows = await contactsAPI.list({
        q: query.trim() || undefined,
        lifecycle_stage: lifecycle || undefined,
        account_id: accountId || undefined,
        limit: 100,
      });
      setResults(rows);
    } catch (e) {
      setLocalErr(e instanceof Error ? e.message : 'Failed to search contacts');
    } finally {
      setLoading(false);
    }
  }, [query, lifecycle, accountId]);

  // Reset + initial load each time the modal opens.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setLifecycle('');
    setAccountId('');
    setSelected(new Map());
    setLocalErr(null);
    void runSearch();
    if (accounts.length === 0) {
      accountsAPI
        .list({ limit: 200 })
        .then(setAccounts)
        .catch(() => undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Escape closes (unless busy).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);

  const toggle = useCallback((c: Contact, checked: boolean) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (checked) next.set(c.id, c);
      else next.delete(c.id);
      return next;
    });
  }, []);

  const accountLookup = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of accounts) map.set(a.id, a.name);
    return map;
  }, [accounts]);

  if (!open) return null;

  const count = selected.size;
  const shownErr = error ?? localErr;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={() => !busy && onClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="contact-picker-title"
    >
      <div
        className="w-full max-w-2xl bg-surface-1 border border-white/[0.16] rounded-ds-xl p-5 shadow-ds-e3 flex flex-col max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="min-w-0">
            <h2 id="contact-picker-title" className="text-lg font-semibold text-slate-50">
              {title}
            </h2>
            {description ? (
              <p className="text-sm text-slate-400 mt-1">{description}</p>
            ) : null}
          </div>
          <IconButton
            intent="ghost"
            size="sm"
            aria-label="Close"
            onClick={() => !busy && onClose()}
            disabled={busy}
          >
            <X size={16} />
          </IconButton>
        </div>

        {/* Filter bar — search first, selects after (DS pattern). */}
        <div className="flex flex-wrap items-end gap-2 mb-3">
          <div className="flex items-center border border-white/[0.08] bg-surface-2 rounded-ds-lg px-2 py-1 focus-within:border-accent transition-colors flex-1 min-w-[12rem]">
            <Search size={14} className="text-slate-400" />
            <input
              type="text"
              placeholder="Name, email, or phone"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void runSearch();
              }}
              disabled={busy}
              className="outline-none px-2 py-1 text-sm w-full bg-transparent text-slate-50 placeholder:text-slate-500"
            />
          </div>
          <Select
            label="Lifecycle"
            value={lifecycle}
            onChange={(e) => setLifecycle(e.target.value as '' | LifecycleStage)}
            disabled={busy}
          >
            <option value="">All stages</option>
            {LIFECYCLE_STAGES.map((s) => (
              <option key={s} value={s}>
                {LIFECYCLE_STAGE_LABELS[s]}
              </option>
            ))}
          </Select>
          <Select
            label="Account"
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            disabled={busy}
          >
            <option value="">All accounts</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Select>
          <Button intent="secondary" onClick={() => void runSearch()} disabled={busy}>
            <Search className="w-4 h-4" strokeWidth={2} />
            Search
          </Button>
        </div>

        {shownErr ? (
          <div className="mb-3 px-3 py-2 rounded-ds-md border border-danger-border bg-danger-tint text-danger-text text-sm">
            {shownErr}
          </div>
        ) : null}

        {/* Results */}
        <div
          className="flex-1 min-h-[10rem] overflow-auto border border-white/[0.08] rounded-ds-md"
          role="listbox"
          aria-multiselectable="true"
          aria-label="Contact picker results"
        >
          {loading ? (
            <p className="text-slate-400 text-sm p-3">Searching…</p>
          ) : results.length === 0 ? (
            <p className="text-slate-400 text-sm p-3">No contacts match.</p>
          ) : (
            results.map((c) => {
              const checked = selected.has(c.id);
              return (
                <label
                  key={c.id}
                  className="flex items-center gap-3 px-3 py-2 hover:bg-white/[0.02] cursor-pointer"
                  role="option"
                  aria-selected={checked}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={busy}
                    onChange={(e) => toggle(c, e.target.checked)}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-100 truncate">{displayName(c)}</p>
                    <p className="text-xs text-slate-400 font-mono truncate">
                      {c.email || c.phone || '—'}
                    </p>
                  </div>
                  {c.account_id ? (
                    <span className="text-xs text-slate-400 truncate max-w-[8rem]">
                      {accountLookup.get(c.account_id) || ''}
                    </span>
                  ) : null}
                  <span className="text-xs text-slate-500">
                    {LIFECYCLE_STAGE_LABELS[c.lifecycle_stage] ?? c.lifecycle_stage}
                  </span>
                </label>
              );
            })
          )}
        </div>

        <div className="flex items-center justify-between gap-2 mt-4">
          <div className="text-sm text-slate-400">
            <span className="font-semibold text-slate-50">{count}</span> selected
            {count > 0 ? (
              <button
                type="button"
                className="ml-3 text-xs text-slate-400 hover:text-slate-200 underline"
                onClick={() => setSelected(new Map())}
                disabled={busy}
              >
                Clear
              </button>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <Button intent="ghost" onClick={() => !busy && onClose()} disabled={busy}>
              Cancel
            </Button>
            <Button
              intent="primary"
              onClick={() => void onConfirm(Array.from(selected.values()))}
              disabled={busy || count === 0}
            >
              {busy ? 'Working…' : `${confirmLabel}${count > 0 ? ` (${count})` : ''}`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPickerModal;
